import { forwardRef, memo, useImperativeHandle, useMemo, useRef } from 'react';
import type { WorkComposerSessionAppearance } from '../../composer/Composer';
import type { ComposerAttachments } from '../../composer/composer-attachments';
import { getWorkHostPort } from '../../work-home/work-home-ports';
import {
  useProjectsComposerChatSession,
  type ProjectsComposerHandle,
} from '../hooks/useProjectsComposerChatSession';
import { InlineClarificationBlock } from './InlineClarificationBlock';
import { ProjectsLandingComposer } from './ProjectsLandingComposer';
import { WorkSessionComposer as WorkSessionComposerView } from './WorkSessionComposer';
import type {
  ProjectsComposerPromptEditorHandle,
  WorkLinkedContextBadgeProps,
  WorkSessionComposerHost,
  WorkSessionComposerProps,
} from './WorkSessionComposer.host';

export type ProjectChatComposerHandle = ProjectsComposerHandle;

const DEFAULT_SESSION_APPEARANCE: WorkComposerSessionAppearance = { kind: 'default' };

/** Host card that reads the evidence LinkedContextBadge at render, not module init. */
function HostLinkedContextBadge(props: WorkLinkedContextBadgeProps) {
  const LinkedContextBadge = getWorkHostPort('evidence').LinkedContextBadge;
  return <LinkedContextBadge {...props} />;
}

/** Host footnote that reads the evidence regulatory footnote at render. */
function HostRegulatoryFootnote(props: { className?: string }) {
  const RegulatoryFootnote =
    getWorkHostPort('evidence').EvidenceRegulatoryFootnoteWithIfu;
  return <RegulatoryFootnote {...props} />;
}

/** Host voice trigger that reads the routines port at render (TDZ-safe). */
function HostVoiceTrigger(props: Record<string, unknown>) {
  const VoiceAssistantTrigger = getWorkHostPort('routines').VoiceAssistantTrigger;
  return <VoiceAssistantTrigger {...props} />;
}

// Module-level so the composer sees one stable host for the life of the app. The
// three factory ports are read when the composer renders, as they always were.
// Card members are render-time wrappers — never getWorkHostPort during object init.
const WORK_SESSION_COMPOSER_HOST: WorkSessionComposerHost = {
  ClarificationBlock: InlineClarificationBlock,
  LinkedContextBadge: HostLinkedContextBadge,
  RegulatoryFootnote: HostRegulatoryFootnote,
  VoiceTrigger: HostVoiceTrigger,
  LinkContext: (options) => ProjectsLandingComposer.LinkContext(options),
  DictateSlot: (options) => ProjectsLandingComposer.DictateSlot(options),
  // Bound directly, not a factory: DictationBar takes no options, so a
  // factory would have nothing to close over.
  DictationBar: ProjectsLandingComposer.DictationBar(),
  PromptEditor: (options) => ProjectsLandingComposer.PromptEditor(options),
};

/**
 * Binds the Work chat composer to what the app owns: the session over the app's
 * inputs, the desktop target, the clipboard paste, the object-refs flag, Chat's
 * image chips and the in-session shortcuts flyout.
 */
const WorkSessionComposer = forwardRef<ProjectsComposerHandle, WorkSessionComposerProps>(
  function WorkSessionComposer(props, ref) {
    const isSubmitting = props.isSubmitting ?? false;
    const sessionAppearance = props.sessionAppearance ?? DEFAULT_SESSION_APPEARANCE;
    const isFocusedAppearance = sessionAppearance.kind === 'focused';
    const contextLocked = props.contextLock != null;
    const canLinkPatientSessions = Boolean(props.patientScope?.patientProfileId);
    const editorRef = useRef<ProjectsComposerPromptEditorHandle | null>(null);
    const desktopTarget = getWorkHostPort('desktop').useDesktopTargetSelection({
      surface: sessionAppearance.kind === 'landing' ? 'landing' : 'project',
      desktopTargetEnabled:
        props.desktopTargetEnabled && !isFocusedAppearance && !contextLocked,
    });

    const chat = useProjectsComposerChatSession({
      ...props,
      isSubmitting,
      editorRef,
      idlePlaceholder:
        sessionAppearance.kind === 'focused' || sessionAppearance.kind === 'default'
          ? sessionAppearance.idlePlaceholder
          : undefined,
      onComposerEmptyChange: props.onSessionDraftEmptyChange,
      idlePlaceholderPreview:
        props.placeholderPreview ??
        (isFocusedAppearance ? sessionAppearance.idlePlaceholderPreview : undefined),
      lockedSessionPatientScope: canLinkPatientSessions ? props.patientScope : null,
    });
    const isComposerLocked = props.runClosed || Boolean(props.readOnly);
    useImperativeHandle(ref, () => chat.handle, [chat.handle]);
    const chatObjectRefsEnabled = getWorkHostPort('shell').useIsChatObjectRefsEnabled();
    const shortcutsFlyout = getWorkHostPort('evidence').useComposerShortcutsFlyout();

    const { handlePaste } = getWorkHostPort('evidence').useChatPaste({
      onFilesDetected: chat.onFilesPicked,
      enabled: !isComposerLocked,
    });

    const ImageFileCard = getWorkHostPort('evidence').ImageFileCard;

    const attachments = useMemo<ComposerAttachments>(
      () => ({
        ...chat.attachments,
        renderImageItem: (item, index) => {
          const file = chat.preUploadedFiles[index];
          if (!file) return null;
          const evidence = getWorkHostPort('evidence');
          return (
            <ImageFileCard
              file={file.file}
              uploadStatus={file.uploadStatus}
              uploadProgress={item.progress}
              previewUrl={evidence.getPreviewUrl(file)}
              errorMessage={item.error}
              onDelete={() => chat.attachments.remove(item.id)}
              onRetry={
                item.status === 'error'
                  ? () => chat.attachments.retry?.(item.id)
                  : undefined
              }
            />
          );
        },
      }),
      [ImageFileCard, chat.attachments, chat.preUploadedFiles]
    );

    return (
      <WorkSessionComposerView
        {...props}
        chat={chat}
        editorRef={editorRef}
        desktopTarget={desktopTarget}
        attachments={attachments}
        onClipboardPaste={handlePaste}
        chatObjectRefsEnabled={chatObjectRefsEnabled}
        shortcutsFlyout={shortcutsFlyout}
        host={WORK_SESSION_COMPOSER_HOST}
      />
    );
  }
);

export const ProjectChatComposer = memo(WorkSessionComposer);
